import type { Presentation } from '@/components/storefront/presentation'
import api, { dataOf, type ApiResponse } from './client'
export interface Package {
  hideDuringCampaign?: boolean
  category?: string
  id: string
  code: string
  name: string
  description: string
  priceFen: number
  points: number
  bonusPoints: number
  bonusValidDays: number | null
  recommended: boolean
  active: boolean
  sortOrder: number
  updatedAt: string
}
export interface Order {
  id: string
  orderNo: string
  workspaceId: string
  userId: string
  userName?: string
  ppid?: string
  productTitle: string
  amountFen: number
  points: number
  status: string
  paymentMethod: string
  providerOrderId: string
  createdAt: string
  expiresAt: string
  paidAt?: string
  creditMissing?: boolean
  creditRepairable?: boolean
  plan?: Package
}
export interface Refund {
  id: string
  orderId: string
  orderNo: string
  productTitle: string
  reason: string
  amountFen: number
  pointsReserved: number
  status: string
  revision: number
  requestedAt: string
  adminNotes: string
}
export interface BillingNotification {
  id: string
  kind: string
  status: string
  orderId: string
  orderNo: string
  productTitle: string
  amountFen: number
  occurredAt: string
}
export interface PointBatchPolicy {
  categoryPriority: string[]
  revision: number
  updatedAt: string
}
export interface BillingRuleVersion {
  catalogSnapshot?: Record<string, unknown>
  version: number
  catalogVersion: string
  pointsPerMillionMicros: number
  estimateSafetyBps: number
  freezeLookupMinutes: number
  publishedBy: number
  reason: string
  publishedAt: string
}
export interface BillingRuleList {
  items: BillingRuleVersion[]
  defaultRate: number
  catalogVersion: string
}
export const billingApi = {
  presentation: () => dataOf(api.get<ApiResponse<Presentation>>('/billing/presentation')),
  savePresentation: (input: Presentation) => dataOf(api.put<ApiResponse<Presentation>>('/billing/presentation',input)),
  billingRules: () => dataOf(api.get<ApiResponse<BillingRuleList>>('/billing/rules')),
  billingRule: (version: number) => dataOf(api.get<ApiResponse<BillingRuleVersion>>(`/billing/rules/${version}`)),
  publishBillingRule: (input: Pick<BillingRuleVersion, 'pointsPerMillionMicros' | 'estimateSafetyBps' | 'freezeLookupMinutes'> & { expectedVersion: number; reason: string }) =>
    dataOf(api.post<ApiResponse<BillingRuleVersion>>('/billing/rules', input)),
  pointBatchPolicy: () => dataOf(api.get<ApiResponse<PointBatchPolicy>>('/billing/point-batch-policy')),
  savePointBatchPolicy: (input: PointBatchPolicy & { reason: string }) =>
    dataOf(api.put<ApiResponse<PointBatchPolicy>>('/billing/point-batch-policy', input)),
  notifications: () =>
    dataOf(
      api.get<ApiResponse<{ items: BillingNotification[] }>>(
        '/billing/notifications',
      ),
    ),
  packages: () =>
    dataOf(api.get<ApiResponse<{ items: Package[] }>>('/billing/packages')),
  savePackage: (p: Package & { reason: string }) =>
    dataOf(
      api.request<ApiResponse<Package>>({
        url: '/billing/packages' + (p.id ? '/' + encodeURIComponent(p.id) : ''),
        method: p.id ? 'PUT' : 'POST',
        data: { ...p, updatedAt: p.updatedAt || undefined },
      }),
    ),
  orders: (page: number, filters: Record<string, string>) =>
    dataOf(
      api.get<ApiResponse<{ items: Order[]; total: number }>>(
        '/billing/orders',
        { params: { page, ...filters } },
      ),
    ),
  order: (id: string) =>
    dataOf(
      api.get<ApiResponse<Order>>('/billing/orders/' + encodeURIComponent(id)),
    ),
  reconcile: (id: string, reason: string) =>
    dataOf(
      api.post<ApiResponse<Order>>(
        `/billing/orders/${encodeURIComponent(id)}/reconcile`,
        { reason },
      ),
    ),
  repairCredit: (id: string, reason: string) =>
    dataOf(
      api.post<ApiResponse<{ id: string; pointsDelta: number }>>(
        `/billing/orders/${encodeURIComponent(id)}/repair-credit`,
        { reason },
      ),
    ),
  refunds: () =>
    dataOf(api.get<ApiResponse<{ items: Refund[] }>>('/billing/refunds')),
  review: (id: string, action: string, reason: string, revision: number) =>
    dataOf(
      api.post<ApiResponse<Refund>>(
        `/billing/refunds/${encodeURIComponent(id)}/review`,
        { action, reason, revision },
      ),
    ),
  export: async (filters: Record<string, string>) => {
    try {
      const r = await api.get('/billing/orders/export', {
        params: filters,
        responseType: 'blob',
      })
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url
      a.download = '充值订单.csv'
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (error) {
      throw new Error('导出失败，请缩小筛选范围后重试（最多5000条）')
    }
  },
}
